import { Component, EventEmitter, HostListener, Input, Output, ChangeDetectionStrategy } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { HttpService, MessageService } from 'src/app/core';
import { PathConfig } from 'src/app/core/config/path.config';
import { Utility } from 'src/app/core/utils/utility';
import { ConfirmModalComponent } from '../confirm-modal/confirm-modal.component';
type Mode = 'light' | 'dark';
interface SelectedValues {
  drawColor: string;
  gridModeColor: {
    light: string;
    dark: string;
  };
  mode: Mode;
  gridSize: number;
  isCleaning: boolean;
  draw: boolean;
};

@Component({
  selector: 'app-pixel-editor',
  templateUrl: './pixel-editor.component.html',
  styleUrl: './pixel-editor.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class PixelEditorComponent {
  @Input() options: any = '';
  @Output() pixelsChanged = new EventEmitter<void>();
  pixelsMap: any = { light: [], dark: [] };
  colorPallete = {
    primary: [
      '#ffffff', '#000000', '#d3d3d3', '#44546A', '#4472c4', '#ed7d31', '#a5a5a5',
      '#ffc000', '#5b9bd5', '#70ad47'
    ],
    variants: [
      ['#ecf0f1', '#d0d3d4', '#b3b6b7', '#909497', '#979a9a'],
      ['#5e5e5e', '#4b4b4b', '#383838', '#252525', '#121212'],
      ['#c0c0c0', '#a6a6a6', '#595959', '#404040', '#262626'],
      ['#AAB4C4', '#7A8CA3', '#44546A', '#2F3D4D', '#1B2733'],
      ['#d0e0f5', '#a0c0ea', '#709fdc', '#1c3c6c', '#102c4f'],
      ['#f7c59f', '#ee9a5c', '#d87d2c', '#b85f1d', '#8c4414'],
      ['#e0e0e0', '#b0b0b0', '#8e8e8e', '#6c6c6c', '#4a4a4a'],
      ['#fff0b3', '#ffe066', '#ffc533', '#e6a800', '#b38600'],
      ['#b7d4f0', '#94c1e4', '#6eaadc', '#1b6499', '#11477a'],
      ['#cbe6b4', '#a2d18a', '#7abf61', '#529d3b', '#3a7429']
    ]
  };
  selectedValues: SelectedValues = {
    drawColor: '#70ad47',
    gridModeColor: {
      light: '#ffffff',
      dark: '#404040'
    },
    mode: 'light',
    gridSize: 20,
    isCleaning: false,
    draw: true,
  };

  previewImages: Record<Mode, string | null> = {
    light: null,
    dark: null
  };

  allModes: Mode[] = ['light', 'dark'];

  isIconCreated: any = {
    light: false,
    dark: false
  };

  @HostListener('window:mousedown') onMouseDown() { this.selectedValues.draw = true; };
  @HostListener('document:mouseup') onMouseUp() {
    if (this.selectedValues.draw) {
      this.selectedValues.draw = false;
      if (this.isIconCreated[this.selectedValues.mode]) {
        this.togglePreview();
      }
    }
  }

  @HostListener('document:dragstart')
  onDragStart() {
    this.selectedValues.draw = false;
  }

  get pixels(): string[] {
    return this.pixelsMap[this.selectedValues.mode];
  }

  set pixels(val: string[]) {
    this.pixelsMap[this.selectedValues.mode] = val;
  }

  constructor(
    private httpService: HttpService,
    private msgService: MessageService,
    private modalService: NgbModal,
    private translateService: TranslateService
  ) { }

  ngOnChanges() {
    this.populatePixels(); // initialize both modes
  }

  populatePixels() {
    this.selectedValues.draw = false;
    this.reset('component');
    if (this.options?.lightRawPixel) {
      this.pixelsMap.light = [...this.options.lightRawPixel];
      this.pixelsMap.dark = [...this.options.darkRawPixel];
      this.allModes.forEach(mode => this.togglePreview(mode));
      this.isIconCreated.light = true;
      this.isIconCreated.dark = true;
      this.selectedValues.drawColor = this.pixelsMap?.[this.selectedValues.mode]?.find((color: any) => color != this.selectedValues?.gridModeColor?.[this.selectedValues.mode]) ?? this.selectedValues.drawColor;
    } else {
      const defaultLight = Array(this.selectedValues.gridSize * this.selectedValues.gridSize).fill(this.selectedValues?.gridModeColor.light);
      const defaultDark = Array(this.selectedValues.gridSize * this.selectedValues.gridSize).fill(this.selectedValues?.gridModeColor.dark);
      this.pixelsMap.light = [...defaultLight];
      this.pixelsMap.dark = [...defaultDark];
    }
  }

  paintPixel(index: number) {
    let selectedMode = this.selectedValues.mode;
    this.isIconCreated[selectedMode] = true;
    const paintColor = this.selectedValues.isCleaning ? this.selectedValues.gridModeColor[selectedMode] : this.selectedValues.drawColor;
    this.pixels[index] = paintColor;
    this.pixels = [...this.pixels]; // trigger change detection
    this.pixelsChanged.emit();
  }

  reset(from?: string) {
    const defaultColor = this.selectedValues?.gridModeColor[this.selectedValues.mode];
    this.pixels = Array(this.selectedValues.gridSize * this.selectedValues.gridSize).fill(defaultColor);
    const mode: Mode = this.selectedValues.mode;
    this.previewImages[mode] = null;
    this.isIconCreated[mode] = false;
    if (from == 'component') {
      if (this.options?.lightRawPixel) {
        this.pixelsMap.light = [...this.options.lightRawPixel];
        this.pixelsMap.dark = [...this.options.darkRawPixel];
        this.allModes.forEach(mode => this.togglePreview(mode));
        this.isIconCreated.light = true;
        this.isIconCreated.dark = true;
      } else {
        this.allModes.forEach(mode => {
          this.previewImages[mode] = null;
          this.isIconCreated[mode] = null;
          this.pixelsMap[mode] = Array(this.selectedValues.gridSize * this.selectedValues.gridSize).fill(this.selectedValues?.gridModeColor[mode]);
        });
      }
    } else {
      this.pixelsChanged.emit();
    }
  }

  toggleCleaning() {
    this.selectedValues.isCleaning = !this.selectedValues.isCleaning;
  }

  downloadImage() {
    const dataUrl = this.generateCanvasDataUrl(20);
    const link = document.createElement('a');
    link.download = `${this.selectedValues.mode}-pixel-art.png`;
    link.href = dataUrl;
    link.click();
  }

  generateCanvasDataUrl(scale: number, mode?: string): string {
    const currentMode = mode || this.selectedValues.mode;
    const pixels = this.pixelsMap[currentMode];
    const size = this.selectedValues.gridSize;

    const canvas = document.createElement('canvas');
    canvas.width = size * scale;
    canvas.height = size * scale;

    const ctx = canvas.getContext('2d')!;
    ctx.imageSmoothingEnabled = true;

    // Clip the drawing area to a circle
    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;
    const radius = canvas.width / 2;

    ctx.beginPath();
    ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
    ctx.closePath();
    ctx.clip(); // All drawing is now limited to the circle

    // Draw pixels
    pixels.forEach((color: string, i: number) => {
      const x = i % size;
      const y = Math.floor(i / size);
      ctx.fillStyle = color;
      ctx.fillRect(x * scale, y * scale, scale, scale);
    });

    return canvas.toDataURL();
  }


  togglePreview(modeParam?: Mode) {
    if (modeParam) {
      this.previewImages[modeParam] = this.generateCanvasDataUrl(10, modeParam);
    } else {
      const mode: Mode = this.selectedValues.mode;
      this.previewImages[mode] = this.generateCanvasDataUrl(10, mode);
    }
  }

  colorSelected(color: string) {
    this.selectedValues.drawColor = color;
  }

  modeSelected(mode: Mode) {
    this.selectedValues.mode = mode;
    this.selectedValues.drawColor = this.pixelsMap?.[mode]?.find((color: any) => color != this.selectedValues?.gridModeColor?.[mode]) ?? this.selectedValues.drawColor;
  }

  async uploadGeneratedImage(iconChangeStatus?: { light: boolean; dark: boolean }) {
    return new Promise(async (resolve, reject) => {
      this.togglePreview();

      const modes = this.allModes.filter(mode =>
        !iconChangeStatus || iconChangeStatus[mode]
      );

      if (modes?.length === 0) {
        resolve({ message: 'No icons to upload' });
        return;
      }

      const uploadPromises = modes.map((mode) => {
        const dataUrl = this.previewImages[mode] || '';
        if (dataUrl) {
          const blob = this.dataURLtoBlob(dataUrl);
          const file = new File([blob], `${mode}-pixel-art.png`, { type: 'image/png' });
          const formData = new FormData();
          formData.append('type', 'FileBuilder');
          formData.append('file', file, file.name);
          return this.httpService.upload(PathConfig.UPLOAD_MEDIA, formData).toPromise().then(response => ({
            mode,
            data: response,
          }));
        } else {
          return;
        }
      });

      try {
        const responses = await Promise.all(uploadPromises);

        const mappedResponses: Record<string, any> = {};
        responses.forEach(res => {
          if (res) {
            mappedResponses[res.mode] = res.data;
          }
        });

        resolve(mappedResponses);
      } catch (err: any) {
        const msgList = Utility.getErrorMessage(err.error || err);
        msgList.forEach((msg) => this.msgService.showError(msg));
        reject(0);
      }
    });
  }

  dataURLtoBlob(dataUrl: string): Blob {
    const arr = dataUrl?.split(',');
    const mime = arr?.[0]?.match(/:(.*?);/)![1];
    const bstr = atob(arr?.[1]);
    let n = bstr?.length;
    const u8arr = new Uint8Array(n);

    while (n--) {
      u8arr[n] = bstr?.charCodeAt(n);
    }

    return new Blob([u8arr], { type: mime });
  }

  checkImageValidation() {
    let errMsg = '';
    if (!this.isIconCreated?.light && !this.isIconCreated?.dark) {
      errMsg = 'Please create light and dark icon.';
    } else if (!this.isIconCreated?.light) {
      errMsg = 'Please create light icon.';
    } else if (!this.isIconCreated?.dark) {
      errMsg = 'Please create dark icon.';
    }
    if (errMsg) {
      this.msgService.showError(errMsg);
      return false;
    } else {
      return true;
    }
  }

  getIconCreationStatus(): { light: boolean; dark: boolean } {
    const light = this.pixelsMap?.light?.some((pixel: any) => pixel !== this.selectedValues?.gridModeColor.light) ?? false;
    const dark = this.pixelsMap?.dark?.some((pixel: any) => pixel !== this.selectedValues?.gridModeColor.dark) ?? false;
    return { light, dark };
  }

  getIconChangeStatus(): { light: boolean; dark: boolean } {
    const light = this.pixelsMap?.light?.some((val: any, i: any) => val !== this.options?.lightRawPixel?.[i]) ?? false;
    const dark = this.pixelsMap?.dark?.some((val: any, i: any) => val !== this.options?.darkRawPixel?.[i]) ?? false;
    return { light, dark };
  }

  getPixelRawData() {
    return this.pixelsMap;
  }

  copyDrawnPixels(fromMode: Mode, toMode: Mode) {
    const modalRef = this.modalService.open(ConfirmModalComponent, {
      ariaLabelledBy: 'modal-basic-title',
      centered: true,
      size: 'sm',
    });
    modalRef.componentInstance.confirmationBoxTitle = this.translateService.instant('Confirmation?');
    modalRef.componentInstance.confirmationMessage =
      `${this.translateService.instant(`Are you sure you want to copy icon from ${fromMode || this.selectedValues.mode} mode to ${toMode || (this.selectedValues.mode == 'dark' ? 'light' : 'dark')} mode?`)}`;
    modalRef.result.then((response) => {
      if (response) {
        let otherMode: Mode = toMode || this.allModes.find((mode: Mode) => mode != this.selectedValues.mode)!;
        let sourcePixels = this.pixelsMap[fromMode || this.selectedValues.mode];
        let targetPixels = [...this.pixelsMap[otherMode]];
        targetPixels = sourcePixels.map((pixel: string, i: number) => {
          const isLightMode = (fromMode || this.selectedValues.mode) === 'light';
          const gridColor = isLightMode
            ? this.selectedValues.gridModeColor.light
            : this.selectedValues.gridModeColor.dark;
          return pixel === gridColor ? this.selectedValues.gridModeColor[otherMode] : pixel;
        });
        this.pixelsMap[otherMode] = targetPixels;
        this.togglePreview(otherMode);
        this.pixelsChanged.emit();
      }
    });
  }

  /**
   * True when pixels differ from loaded options (existing design)
   * or from a blank grid (new / no saved raw pixel data).
   */
  hasUnsavedPixelChanges(): boolean {
    if (this.options?.lightRawPixel) {
      const status = this.getIconChangeStatus();
      return !!(status.light || status.dark);
    }
    const status = this.getIconCreationStatus();
    return !!(status.light || status.dark);
  }

  ngOnDestroy(): void {}
}
